// =============================================================================
// features/sales/campaigns/wizard/useWizardHydration.ts — Rehidrata o
// objetivo/distribuição/cadência a partir de `GET /api/sales/campaigns/:id`
// quando a tela abre com `?draft=<id>` (reload, link direto, "voltei
// depois"). Extraído de WizardContent.tsx (norma anti-padrão: componente
// acima de 200 linhas).
//
// Só hidrata 1x por montagem (`hydratedRef`) — depois disso, o estado local
// do formulário manda, nunca um refetch por trás sobrescreve o que o gestor
// está digitando.
// =============================================================================

import * as React from 'react';

import { useCampaignDraft } from '../../../../hooks/sales/useCampaignWizard';

import { EMPTY_OBJECTIVE } from './types';
import type { CadenceValues, DistributionValues, ObjectiveValues } from './types';

export interface UseWizardHydrationResult {
  /** `true` quando `?draft=<id>` apontava para uma campanha inexistente/fora de escopo. */
  notFound: boolean;
}

export function useWizardHydration(
  campaignId: string | null,
  setObjective: (v: ObjectiveValues) => void,
  setDistribution: (v: DistributionValues) => void,
  setCadence: (v: CadenceValues) => void,
): UseWizardHydrationResult {
  const hydratedRef = React.useRef(false);
  const [notFound, setNotFound] = React.useState(false);

  const draftQuery = useCampaignDraft(campaignId, {
    enabled: campaignId !== null && !hydratedRef.current,
  });

  React.useEffect(() => {
    if (hydratedRef.current || !draftQuery.data) return;
    hydratedRef.current = true;
    const c = draftQuery.data;
    setObjective({
      ...EMPTY_OBJECTIVE,
      cityId: c.cityId,
      name: c.name,
      description: c.description ?? '',
      productId: c.productId ?? '',
      priority: c.priority,
    });
    setDistribution({ assignmentStrategy: c.assignmentStrategy });
    setCadence({
      weeklyWindow: c.weeklyWindow,
      timezone: c.timezone,
      maxAttempts: c.maxAttempts,
      minIntervalMinutes: c.minIntervalMinutes,
    });
    // setObjective/setDistribution/setCadence são setState estáveis (identidade
    // não muda entre renders) — não precisam entrar nas dependências.
  }, [draftQuery.data]);

  React.useEffect(() => {
    if (campaignId !== null && draftQuery.isError && !hydratedRef.current) {
      hydratedRef.current = true;
      setNotFound(true);
    }
  }, [draftQuery.isError, campaignId]);

  return { notFound };
}
